import React, { useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// "Send" / "Resend" for a generated proposal. The backend issues a private
// proposal link and emails it; this shows exactly what happened - including
// when nothing was emailed (SendGrid not configured, no address on file) -
// and always offers the link to copy.
export default function ProposalSendAction({ proposal, onSent }) {
  const [open, setOpen] = useState(false);
  const [recipient, setRecipient] = useState(proposal.recipient_email || "");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  if (!["Draft", "Sent"].includes(proposal.status)) return null;

  const send = async () => {
    setSending(true);
    setResult(null);
    setCopied(false);
    const token = localStorage.getItem("msb_token");
    try {
      const res = await fetch(`${BACKEND_URL}/api/proposals/${proposal.id}/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(recipient.trim() ? { recipient_email: recipient.trim() } : {}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = typeof data.detail === "string" ? data.detail : "Failed to send proposal";
        setResult({ error: detail });
      } else {
        setResult(data);
        if (onSent) onSent(data);
      }
    } catch (err) {
      setResult({ error: "Failed to send proposal" });
    }
    setSending(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.proposal_link);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <>
      <button className="ghost-btn small" data-testid={`proposal-send-${proposal.id}-button`} onClick={() => setOpen(true)}>
        {proposal.status === "Sent" ? "Resend / Link" : "Send"}
      </button>
      {open && (
        <div className="drawer-backdrop" onClick={() => setOpen(false)}>
          <div className="record-drawer glass-panel" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()} data-testid={`proposal-send-${proposal.id}-drawer`}>
            <div className="drawer-header"><h2>Send proposal - {proposal.clinic_name}</h2></div>
            <p className="muted">The clinic gets a private link to view, accept and pay. {proposal.status === "Sent" ? "Resending replaces the previous link." : ""}</p>
            <label>
              Recipient email (optional - defaults to the lead/client email on file)
              <input data-testid={`proposal-send-${proposal.id}-recipient-input`} type="email" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="owner@clinic.com" />
            </label>
            {result?.error && <p className="error-message" data-testid={`proposal-send-${proposal.id}-error`}>{result.error}</p>}
            {result && !result.error && (
              <div data-testid={`proposal-send-${proposal.id}-result`}>
                <p className={result.email_sent ? "success-text" : "error-message"}>{result.message}</p>
                <label>
                  Proposal link
                  <input readOnly value={result.proposal_link} onFocus={(e) => e.target.select()} data-testid={`proposal-send-${proposal.id}-link`} />
                </label>
                <button className="ghost-btn small" onClick={copy} data-testid={`proposal-send-${proposal.id}-copy`}>{copied ? "Copied" : "Copy link"}</button>
                {result.link_expires_at && <span className="muted"> Expires {new Date(result.link_expires_at).toLocaleDateString()}</span>}
              </div>
            )}
            <div className="drawer-footer">
              <button className="ghost-btn" onClick={() => setOpen(false)}>Close</button>
              <button className="primary-btn" disabled={sending} onClick={send} data-testid={`proposal-send-${proposal.id}-confirm`}>
                {sending ? "Sending..." : result && !result.error ? "Send again" : "Send proposal"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
